import { useState, useEffect } from 'react';
import { ILanguage, LANGUAGES, extractTextFromResponse } from '../utils';
import { LANGUAGE_EN, LANGUAGE_CN } from '@home/_config/config';

type FetchObjectParams = {
  key: string;
  responseType?: 'text' | 'arraybuffer' | 'blob';
};

type GetObjectFn = (params: FetchObjectParams) => Promise<unknown>;

export interface UseLowcodeCosTemplateParams {
  templateConfig: IRecord;
  isCOSInited: boolean;
  getObject: GetObjectFn;
}

export interface UseLowcodeCosTemplateResult {
  loading: boolean;
  error?: unknown;
  htmlContentMap: IRecord<string>;
}

const TEMPLATE_PATH_KEY_MAP: Record<ILanguage, string> = {
  [LANGUAGE_EN]: 'en_templatePath',
  [LANGUAGE_CN]: 'zh_templatePath',
};

const createEmptyHtmlContentMap = (): Record<ILanguage, string> => {
  return {
    [LANGUAGE_EN]: '',
    [LANGUAGE_CN]: '',
  };
};

export default function useLowcodeCosTemplate(props: UseLowcodeCosTemplateParams): UseLowcodeCosTemplateResult {
  const { templateConfig, isCOSInited, getObject } = props;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [htmlContentMap, setHtmlContentMap] = useState<Record<ILanguage, string>>(createEmptyHtmlContentMap);

  const fetchTemplates = async (config?: IRecord) => {
    setLoading(true);
    setError(null);

    const newHtmlContentMap = createEmptyHtmlContentMap();
    const errors: Error[] = [];

    // 并行获取所有语言的模板
    await Promise.all(
      LANGUAGES.map(async (language) => {
        const templatePathKey = TEMPLATE_PATH_KEY_MAP[language];
        const templatePath = config?.[templatePathKey] as string | undefined;

        if (!templatePath) {
          return;
        }

        try {
          const body = await getObject({
            key: templatePath,
            responseType: 'text',
          });
          newHtmlContentMap[language] = await extractTextFromResponse(body);
        } catch (err) {
          console.error(`Failed to fetch template for ${language}:`, err);
          errors.push(err instanceof Error ? err : new Error(String(err)));
        }
      }),
    );

    // 当所有语言的模板都获取失败时才设置错误
    if (errors.length === LANGUAGES.length) {
      setError(errors[0]);
      setHtmlContentMap(newHtmlContentMap);
      setLoading(false);
      return;
    }

    // 降级处理：用成功获取的语言模板补充失败的语言
    if (errors.length > 0 && errors.length < LANGUAGES.length) {
      const successLanguage = LANGUAGES.find((lang) => newHtmlContentMap[lang]);
      if (successLanguage) {
        const fallbackContent = newHtmlContentMap[successLanguage];
        LANGUAGES.forEach((lang) => {
          if (!newHtmlContentMap[lang]) {
            newHtmlContentMap[lang] = fallbackContent;
          }
        });
      }
    }

    setHtmlContentMap(newHtmlContentMap);
    setLoading(false);
  };

  useEffect(() => {
    if (!isCOSInited) {
      return;
    }

    void fetchTemplates(templateConfig);
  }, [isCOSInited, templateConfig]);

  return {
    loading,
    error,
    htmlContentMap,
  };
}
